import React, { useState, useEffect } from "react";
import {
  Plus,
  Folder,
  FolderPlus,
  Users,
  LayoutGrid,
  List,
  Search,
  Database,
  X,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const Home = () => {
  const [view, setView] = useState("grid");
  const [projects, setProjects] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [teamMember, setTeamMember] = useState({
    email: "",
    password: "",
    role: "user",
  });
  const [userRole, setUserRole] = useState("admin"); // ideally from JWT decode
  const [userLimit, setUserLimit] = useState(0);
  const [teamCount, setTeamCount] = useState(0);
  const [companyId, setCompanyId] = useState("");
  const [teamId, setTeamId] = useState("");
  const navigate = useNavigate();

  // Logout
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
  };

  // Load saved projects
  useEffect(() => {
    const savedProjects = JSON.parse(localStorage.getItem("projects")) || [];
    setProjects(savedProjects);
  }, []);

  // Fetch company info and team data
  useEffect(() => {
    const fetchCompanyAndTeam = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        // Fetch company info
        const companyRes = await fetch(
          "https://redian-ai-architecture.api.redianglobal.com//api/companies/my-company",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const companyData = await companyRes.json();
        setCompanyId(companyData._id);
        setUserLimit(companyData.maxUsers);

        // Fetch teams for this company
        const teamRes = await fetch("https://redian-ai-architecture.api.redianglobal.com//api/teams", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const teams = await teamRes.json();
        let companyTeam = teams.find(
          (team) => team.companyId === companyData._id
        );

        // If no team exists, create default team automatically
        if (!companyTeam) {
          const createTeamRes = await fetch("https://redian-ai-architecture.api.redianglobal.com//api/teams", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              name: "Default Team",
              companyId: companyData._id,
            }),
          });
          companyTeam = await createTeamRes.json();
        }

        setTeamId(companyTeam._id);
        setTeamCount(companyTeam.users?.length || 0);
      } catch (err) {
        console.error("Error fetching company/team info:", err);
      }
    };

    fetchCompanyAndTeam();
  }, []);

  // Invite new team member
  const handleInvite = async (e) => {
    e.preventDefault();
    if (!teamId) return alert("Team not loaded yet");

    try {
      const token = localStorage.getItem("token");
      const res = await fetch("https://redian-ai-architecture.api.redianglobal.com//api/teams/add-user", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          teamId,
          email: teamMember.email,
          password: teamMember.password,
          role: teamMember.role,
          firstName: teamMember.firstName || "First", // default placeholder
          lastName: teamMember.lastName || "Last", // default placeholder
        }),
      });

      const data = await res.json();

      if (res.ok) {
        alert("Team member added successfully!");
        setShowInviteModal(false);
        setTeamMember({ email: "", password: "", role: "user" });

        // Refresh team data
        const teamRes = await fetch(`https://redian-ai-architecture.api.redianglobal.com//api/teams`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const teams = await teamRes.json();
        const companyTeam = teams.find((team) => team._id === teamId);
        if (companyTeam) setTeamCount(companyTeam.users?.length || 0);
      } else {
        alert(data.message || "Error adding user");
      }
    } catch (err) {
      console.error("Server error:", err);
      alert("Server error. Please try again later.");
    }
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-[#0d1b2a] to-[#1a2d45] text-white">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0d1b2a]/80 backdrop-blur-lg border-r border-[#2d3e56] flex flex-col">
        <div className="flex items-center px-6 py-6 border-b border-[#2d3e56]">
          <img
            src="/assets/images/rlogo.png"
            alt="Redian Logo"
            className="h-12 w-100 object-contain mr-3"
          />
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1">
          <button
            onClick={() => navigate("/create")}
            className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 py-3 rounded-lg mb-6 transition-all duration-200 shadow-lg shadow-blue-600/20"
          >
            <Plus size={18} />
            <span className="font-medium">Create Project</span>
          </button>

          <Link
            to="/home"
            className="flex items-center space-x-3 px-3 py-3 rounded-lg bg-blue-600/20 border border-blue-500/30 cursor-pointer group transition-colors"
          >
            <Folder size={18} className="text-blue-400" />
            <span className="font-medium">Projects</span>
          </Link>

          <Link
            to="/templates"
            className="flex items-center space-x-3 px-3 py-3 rounded-lg hover:bg-[#2d3e56] cursor-pointer group transition-colors"
          >
            <FolderPlus
              size={18}
              className="text-gray-400 group-hover:text-white"
            />
            <span className="text-gray-300 group-hover:text-white">
              Templates
            </span>
          </Link>

          <Link
            to="/assemblies"
            className="flex items-center space-x-3 px-3 py-3 rounded-lg hover:bg-[#2d3e56] cursor-pointer group transition-colors"
          >
            <Database
              size={18}
              className="text-gray-400 group-hover:text-white"
            />
            <span className="text-gray-300 group-hover:text-white">
              Assemblies
            </span>
          </Link>
        </nav>

        <div className="px-4 py-4 border-t border-[#2d3e56]">
          <div className="text-sm text-gray-400 font-medium mb-2">
            Demo Projects
          </div>
          <div className="space-y-2">
            <div className="text-xs text-gray-500 hover:text-gray-300 cursor-pointer py-1 transition-colors">
              Architecture Concept
            </div>
            <div className="text-xs text-gray-500 hover:text-gray-300 cursor-pointer py-1 transition-colors">
              Urban Planning
            </div>
            <div className="text-xs text-gray-500 hover:text-gray-300 cursor-pointer py-1 transition-colors">
              Landscape Design
            </div>
          </div>
          <div className="mt-6">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-red-400 border border-red-500/40 rounded-lg hover:bg-red-500/10 hover:text-red-300 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-8 py-4 border-b border-[#2d3e56] bg-[#0d1b2a]/50 backdrop-blur-sm">
          <div className="flex-1 h-16 max-w-2xl relative">
            <Search
              size={18}
              className="absolute left-3 top-6 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search projects or folders..."
              className="w-full pl-10 pr-4 py-3 bg-[#1b263b] border border-[#2d3e56] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
            />
          </div>
          <div className="flex items-center space-x-4 ml-6">
            {userRole === "admin" && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-600/20"
              >
                <Users size={16} />
                <span className="font-medium">Invite Team</span>
              </button>
            )}
            <div className="flex items-center space-x-3">
              <div className="h-9 w-16 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center font-semibold shadow-lg">
                Admin
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 p-8 overflow-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-2 text-sm">
              <span className="text-gray-400">Home</span>
              <span className="text-gray-600">/</span>
              <span className="text-white font-medium">Projects</span>
            </div>
            <div className="flex items-center space-x-3">
              <select className="bg-[#1b263b] border border-[#2d3e56] text-sm rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all">
                <option>My projects</option>
                <option>Team projects</option>
                <option>Archived projects</option>
              </select>
              <div className="flex space-x-1 bg-[#1b263b] border border-[#2d3e56] rounded-xl p-1">
                <button
                  onClick={() => setView("grid")}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    view === "grid"
                      ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  <LayoutGrid size={18} />
                </button>
                <button
                  onClick={() => setView("list")}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    view === "list"
                      ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  <List size={18} />
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {projects.length === 0 ? (
              <div className="flex flex-col items-center justify-center col-span-full text-center py-20">
                <Folder size={48} className="text-blue-400 mb-4" />
                <h3 className="text-2xl font-bold mb-2">No projects found</h3>
                <p className="text-gray-400 mb-8">
                  Get started by creating your first project or exploring
                  templates
                </p>
              </div>
            ) : (
              projects.map((project, index) => (
                <Link to={`/project/${project.projectId}`} key={index}>
                  <div className="p-6 rounded-2xl bg-[#1b263b] border border-[#2d3e56] hover:border-blue-500/50 hover:bg-[#2d3e56] transition-all duration-200">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-white">{project.name}</h4>
                    </div>
                    {project.fileUrl &&
                    project.fileName.toLowerCase().endsWith(".pdf") ? (
                      <iframe
                        src={project.fileUrl}
                        title={project.name}
                        className="w-full h-40 object-cover rounded-lg mb-4"
                        style={{ border: "none" }}
                      />
                    ) : project.fileUrl ? (
                      <img
                        src={project.fileUrl}
                        alt={project.name}
                        className="w-full h-40 object-cover rounded-lg mb-4"
                      />
                    ) : null}
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </main>

      {showInviteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#1b263b] rounded-xl p-6 w-full max-w-md border border-blue-500/30 relative">
            <button
              onClick={() => setShowInviteModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-white"
            >
              <X size={20} />
            </button>
            <h3 className="text-xl font-semibold mb-4">Invite Team Member</h3>
            <p className="text-sm text-gray-400 mb-4">
              You can add up to {userLimit} users. Current: {teamCount}
            </p>

            <form onSubmit={handleInvite} className="space-y-4">
              <input
                type="text"
                placeholder="First Name"
                value={teamMember.firstName || ""}
                onChange={(e) =>
                  setTeamMember({ ...teamMember, firstName: e.target.value })
                }
                required
                className="w-full bg-[#0d1b2a] border border-[#2d3e56] text-white rounded-md px-3 py-2"
              />
              <input
                type="text"
                placeholder="Last Name"
                value={teamMember.lastName || ""}
                onChange={(e) =>
                  setTeamMember({ ...teamMember, lastName: e.target.value })
                }
                required
                className="w-full bg-[#0d1b2a] border border-[#2d3e56] text-white rounded-md px-3 py-2"
              />

              <input
                type="email"
                placeholder="Email"
                value={teamMember.email}
                onChange={(e) =>
                  setTeamMember({ ...teamMember, email: e.target.value })
                }
                required
                className="w-full bg-[#0d1b2a] border border-[#2d3e56] text-white rounded-md px-3 py-2"
              />
              <input
                type="password"
                placeholder="Password"
                value={teamMember.password}
                onChange={(e) =>
                  setTeamMember({ ...teamMember, password: e.target.value })
                }
                required
                className="w-full bg-[#0d1b2a] border border-[#2d3e56] text-white rounded-md px-3 py-2"
              />
              <select
                value={teamMember.role}
                onChange={(e) =>
                  setTeamMember({ ...teamMember, role: e.target.value })
                }
                className="w-full bg-[#0d1b2a] border border-[#2d3e56] text-white rounded-md px-3 py-2"
              >
                <option value="manager">Manager</option>
                <option value="user">User</option>
                <option value="supervisor">Supervisor</option>
              </select>
              <button
                type="submit"
                disabled={teamCount >= userLimit}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md font-medium disabled:opacity-50"
              >
                Add Member
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
